import React, { useEffect, useMemo, useState } from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { getConfig, getHomeRoutes } from "@companion-core/shared/app/Utils/config";
import { TabBarItem } from "@companion-core/shared/app/Interfaces/tabBar";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { trackClickBell } from "@companion-core/shared/app/tracking/bell";
import "@companion-core/web/src/Assets/Styles/scss/tabBar.scss";
import { IntroductionPopover } from "@companion-core/shared/app/Components/Bell/IntroductionPopover";
import { isTabBarVisible } from "@companion-core/web/src/Utilities/tabBar";

export const TabBar = () => {
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = getConfig();
  const options = config?.options;
  const modules = config?.modules;
  const { logged: homeRouteLogged } = getHomeRoutes();
  const bellModule = useMemo(() => modules?.concierge?.modules?.bell, [modules]);
  const isBellEnabled: boolean = bellModule?.enabled;
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const [scrolling, setScrolling] = useState<boolean>(false);
  const { isPopUpVisible, setIsPopUpVisible } = useBellStore((state) => ({
    isPopUpVisible: state.isPopUpVisible,
    setIsPopUpVisible: state.setIsPopUpVisible,
  }));
  const { setIsIntroPopUpVisible, isIntroPopUpVisible } = useBellStore((state) => ({
    isIntroPopUpVisible: state.isIntroPopUpVisible,
    setIsIntroPopUpVisible: state.setIsIntroPopUpVisible,
  }));
  let scrollShadowTimeout: NodeJS.Timeout;

  const isDisplayed = isTabBarVisible(options);

  const handleOnScroll = () => {
    clearTimeout(scrollShadowTimeout);
    setScrolling(true);
    scrollShadowTimeout = setTimeout(() => setScrolling(false), 500);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleOnScroll);

    return () => {
      window.removeEventListener("scroll", handleOnScroll);
    };
  }, []);

  const isCurrentLocation = (pathname: string) => {
    if (pathname === homeRouteLogged) {
      return `/${homeRouteLogged}` === location.pathname || "/" === location.pathname;
    }
    return `/${pathname}` === location.pathname;
  };

  const getModifier = (className: string, isMiddle: boolean, pathname: string) => {
    if (isMiddle) {
      return `${className}--middle`;
    } else if (isCurrentLocation(pathname)) {
      return `${className}--focused`;
    }
    return "";
  };

  const toggleBell = () => {
    setIsPopUpVisible(!isPopUpVisible);
    trackClickBell(isPopUpVisible ? "Reduce_Bell" : "Open_Bell");
  };

  const TabBarComponent = ({ item, index }: { item: TabBarItem; index: number }) => {
    return (
      <button
        key={index}
        data-testid={`tab-bar__item${item.name === "concierge" ? "-bell" : ""}`}
        className={`tab-bar__item ${getModifier("tab-bar__item", item.isMiddle, item.slugPage)}`}
        aria-label={t(`common.tab-bar.accessibility.button.${item.name}`)}
        aria-haspopup={item.name === "concierge" ? "dialog" : undefined}
        aria-expanded={item.name === "concierge" ? isPopUpVisible : undefined}
        aria-controls={
          item.name === "concierge" ? (isPopUpVisible ? "bell-popover" : undefined) : undefined
        }
        onClick={() => {
          if (item.name === "concierge") {
            toggleBell();
            if (isIntroPopUpVisible) setIsIntroPopUpVisible(false);
          } else {
            navigate(item.slugPage);
          }
        }}>
        <FontIcon
          className={`tab-bar__item-icon ${getModifier("tab-bar__item-icon", false, item.slugPage)}`}
          name={item.icon}
          size={24}
        />
        {!item.isMiddle && (
          <span
            className={`tab-bar__item-label ${
              isCurrentLocation(item.slugPage) ? "body-text-9" : "body-text-10"
            }`}>
            {t(`common.tabBar.links.${item.name}`)}
          </span>
        )}
      </button>
    );
  };

  return (
    isDisplayed &&
    authenticated &&
    isMobileSize && (
      <div data-testid="tab-bar" className={`tab-bar ${scrolling && "tab-bar--scrolling"}`}>
        {options.tabBar.map((item: TabBarItem, index: number) =>
          item.name === "concierge" ? (
            isBellEnabled ? (
              <Popover
                key={index}
                placement="top"
                open={
                  isIntroPopUpVisible &&
                  isMobileSize &&
                  !bellModule?.options?.isPopUpVisibleByDefault
                }
                classNames={{ root: "bell-intro-popover" }}
                content={<IntroductionPopover onClose={() => setIsIntroPopUpVisible(false)} />}>
                <TabBarComponent index={index} item={item} key={index} />
              </Popover>
            ) : (
              <></>
            )
          ) : (
            <TabBarComponent index={index} item={item} key={index} />
          )
        )}
      </div>
    )
  );
};
